import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { ContactDialog } from '@/components/ContactDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUser } from '@/contexts/UserContext';
import { cn } from '@/lib/utils';
import { trackingAPI } from '@/lib/api';
import {
  Plane,
  Ship,
  Search,
  ShieldCheck,
  FileCheck,
  MapPin,
  ArrowRight,
  CheckCircle,
  Globe,
  Users,
  Package,
  Award,
  UserCircle,
  LogOut,
  User
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export default function Home() {
  const { language, setLanguage, t, dir } = useLanguage();
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState<any>(null);
  const [trackingError, setTrackingError] = useState('');
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  // No redirect - allow logged in users to view home page

  // Handle tracking search
  const handleTrackShipment = async () => {
    setTrackingError('');
    setTrackingResult(null);

    if (!trackingNumber.trim()) {
      setTrackingError('Please enter a shipment number');
      return;
    }

    try {
      // Use public tracking API (no authentication required)
      const response = await trackingAPI.track(trackingNumber.trim());
      
      if (response.success && response.data) {
        setTrackingResult(response.data);
      } else {
        setTrackingError('Shipment not found. Please check the number and try again.');
      }
    } catch (error: any) {
      // Show user-friendly error message
      setTrackingError(error.message || 'Shipment not found. Please check the number and try again.');
      console.error('Tracking error:', error);
    }
  };

  const services = [
    {
      icon: Plane,
      title: t('airFreight'),
      description: t('airFreightDesc'),
      color: 'bg-blue-50 text-[#1e3a6e]'
    },
    {
      icon: Ship,
      title: t('seaFreight'),
      description: t('seaFreightDesc'),
      color: 'bg-orange-50 text-[#f5a623]'
    },
    {
      icon: Search,
      title: t('sourcing'),
      description: t('sourcingDesc'),
      color: 'bg-blue-50 text-[#1e3a6e]'
    },
    {
      icon: ShieldCheck,
      title: t('inspection'),
      description: t('inspectionDesc'),
      color: 'bg-orange-50 text-[#f5a623]'
    },
    {
      icon: FileCheck,
      title: t('customsClearance'),
      description: t('customsClearanceDesc'),
      color: 'bg-blue-50 text-[#1e3a6e]'
    },
    {
      icon: MapPin,
      title: t('tracking'),
      description: t('trackingDesc'),
      color: 'bg-orange-50 text-[#f5a623]'
    }
  ];

  const stats = [
    {
      icon: Award,
      value: '15+',
      label: t('yearsExperience')
    },
    {
      icon: Users,
      value: '5000+',
      label: t('satisfiedClients')
    },
    {
      icon: Package,
      value: '50000+',
      label: t('shipmentsDelivered')
    },
    {
      icon: Globe,
      value: '100+',
      label: t('globalNetwork')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PublicHeader onContactClick={() => setIsContactDialogOpen(true)} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1e3a6e] via-[#2d4f8f] to-[#1e3a6e] py-24">
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/20 to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" dir={dir}>
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('welcomeTo')} <span className="text-[#f5a623]">SourceMyGoods</span>
            </h1>
            <h2 className="text-2xl md:text-3xl text-blue-100 mb-8 font-light">
              {t('professionalSourcing')}
            </h2>
            <p className="text-lg text-blue-100 mb-12 max-w-2xl mx-auto">
              {t('homeTagline')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-[#f5a623] hover:bg-[#e09416] text-white px-8">
                <Link to="/register" className="flex items-center">
                  {t('getStarted')}
                  <ArrowRight className={cn("h-4 w-4", dir === 'rtl' ? 'mr-2' : 'ml-2')} />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1e3a6e] px-8" onClick={() => setIsContactDialogOpen(true)}>
                {t('requestQuote')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Shipment Tracking Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" dir={dir}>
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('trackYourShipment')}</h2>
            <p className="text-lg text-gray-600">
              {t('enterShipmentNumber')}
            </p>
          </div>

          <Card className="shadow-lg">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Input
                  type="text"
                  placeholder={t('shipmentNumber')}
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTrackShipment()}
                  className="flex-1 text-lg"
                />
                <Button
                  onClick={handleTrackShipment}
                  className="bg-[#1e3a6e] hover:bg-[#2d4f8f] text-white whitespace-nowrap"
                  size="lg"
                >
                  <Search className={cn("h-5 w-5", dir === 'rtl' ? 'ml-2' : 'mr-2')} />
                  {t('trackShipment')}
                </Button>
              </div>

              {/* Tracking Error */}
              {trackingError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-600 text-center">{trackingError}</p>
                </div>
              )}

              {/* Tracking Result */}
              {trackingResult && (
                <div className="bg-blue-50 border-2 border-[#1e3a6e]/20 rounded-lg p-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-[#1e3a6e]/20 pb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{trackingResult.shipmentNumber}</h3>
                      <p className="text-sm text-gray-600">{trackingResult.customerName}</p>
                    </div>
                    <div className={cn(
                      "px-4 py-2 rounded-full text-sm font-semibold",
                      trackingResult.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                      trackingResult.status === 'In Transit' ? 'bg-orange-100 text-[#f5a623]' :
                      trackingResult.status === 'At Destination' ? 'bg-blue-100 text-[#1e3a6e]' :
                      'bg-yellow-100 text-yellow-800'
                    )}>
                      {trackingResult.status}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('destination')}</p>
                      <p className="font-semibold text-gray-900">{trackingResult.destinationWarehouse}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('freightMethod')}</p>
                      <p className="font-semibold text-gray-900">{trackingResult.freightMethod}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('weight')}</p>
                      <p className="font-semibold text-gray-900">{trackingResult.weight} kg</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('packages')}</p>
                      <p className="font-semibold text-gray-900">{trackingResult.packages}</p>
                    </div>
                    {trackingResult.estimatedDelivery && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-500">{t('estimatedDelivery')}</p>
                        <p className="font-semibold text-gray-900">{trackingResult.estimatedDelivery}</p>
                      </div>
                    )}
                  </div>

                  {/* Status Timeline */}
                  <div className="pt-4 border-t border-[#1e3a6e]/20">
                    <p className="text-sm font-semibold text-gray-700 mb-3">{t('shipmentProgress')}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col items-center flex-1">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center mb-2",
                          ['Processing', 'In Transit', 'At Destination', 'Delivered'].includes(trackingResult.status)
                            ? 'bg-[#1e3a6e] text-white'
                            : 'bg-gray-200 text-gray-400'
                        )}>
                          <CheckCircle className="h-5 w-5" />
                        </div>
                        <p className="text-xs text-center">{t('processing')}</p>
                      </div>
                      <div className="flex-1 h-1 bg-gray-200">
                        <div className={cn(
                          "h-full transition-all duration-500",
                          ['In Transit', 'At Destination', 'Delivered'].includes(trackingResult.status)
                            ? 'bg-[#1e3a6e] w-full'
                            : 'bg-gray-200 w-0'
                        )} />
                      </div>
                      <div className="flex flex-col items-center flex-1">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center mb-2",
                          ['In Transit', 'At Destination', 'Delivered'].includes(trackingResult.status)
                            ? 'bg-[#1e3a6e] text-white'
                            : 'bg-gray-200 text-gray-400'
                        )}>
                          <CheckCircle className="h-5 w-5" />
                        </div>
                        <p className="text-xs text-center">{t('inTransit')}</p>
                      </div>
                      <div className="flex-1 h-1 bg-gray-200">
                        <div className={cn(
                          "h-full transition-all duration-500",
                          ['At Destination', 'Delivered'].includes(trackingResult.status)
                            ? 'bg-[#1e3a6e] w-full'
                            : 'bg-gray-200 w-0'
                        )} />
                      </div>
                      <div className="flex flex-col items-center flex-1">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center mb-2",
                          ['At Destination', 'Delivered'].includes(trackingResult.status)
                            ? 'bg-[#1e3a6e] text-white'
                            : 'bg-gray-200 text-gray-400'
                        )}>
                          <CheckCircle className="h-5 w-5" />
                        </div>
                        <p className="text-xs text-center">{t('atDestination')}</p>
                      </div>
                      <div className="flex-1 h-1 bg-gray-200">
                        <div className={cn(
                          "h-full transition-all duration-500",
                          trackingResult.status === 'Delivered'
                            ? 'bg-[#1e3a6e] w-full'
                            : 'bg-gray-200 w-0'
                        )} />
                      </div>
                      <div className="flex flex-col items-center flex-1">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center mb-2",
                          trackingResult.status === 'Delivered'
                            ? 'bg-[#1e3a6e] text-white'
                            : 'bg-gray-200 text-gray-400'
                        )}>
                          <CheckCircle className="h-5 w-5" />
                        </div>
                        <p className="text-xs text-center">{t('delivered')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" dir={dir}>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('ourServices')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('comprehensiveLogistics')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg ${service.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription className="text-gray-600">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="ghost" className="text-[#f5a623] hover:text-[#e09416] p-0">
                    {t('learnMore')}
                    <ArrowRight className={cn("h-4 w-4", dir === 'rtl' ? 'mr-2' : 'ml-2')} />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1e3a6e] to-[#2d4f8f]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" dir={dir}>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('readyToStreamline')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t('joinThousands')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#f5a623] hover:bg-[#e09416] text-white px-8"
              onClick={() => setIsContactDialogOpen(true)}
            >
              {t('contactUs')}
              <ArrowRight className={cn("h-4 w-4", dir === 'rtl' ? 'mr-2' : 'ml-2')} />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-[#1e3a6e] px-8"
              onClick={() => setIsContactDialogOpen(true)}
            >
              {t('learnMore')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />

      {/* Contact Dialog */}
      <ContactDialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen} />
    </div>
  );
}